/**
 * Lecture du consentement marketing du visiteur.
 *
 * Sert à décider si l'achat peut être doublé d'un événement serveur vers Meta
 * (Conversions API) : on envoie des données personnelles à un tiers à des fins
 * publicitaires, donc uniquement avec le consentement de la personne.
 *
 * Le plugin n'embarque pas de bandeau de cookies : c'est le site qui en pose
 * un. On lit donc les signaux standards, dans l'ordre de fiabilité, et on
 * répond `false` par défaut. Un site sans bandeau n'envoie rien, ce qui est le
 * comportement voulu.
 *
 * Signaux reconnus :
 *  1. `window.mondayMindbodyConsent` — point d'accroche explicite pour une
 *     solution qui ne parlerait aucun des standards ci-dessous. À poser
 *     par le site (booléen, ou fonction renvoyant un booléen).
 *  2. Cookiebot — `window.Cookiebot` (alias `window.CookieConsent`), posé par
 *     son script uc.js, quel que soit son mode d'installation (extension
 *     WordPress ou balise GTM). Voir readCookiebot().
 *  3. WP Consent API — le standard WordPress : le bandeau du site y déclare
 *     le consentement, les extensions le lisent. Voir la garde importante
 *     dans readWpConsentApi().
 *  4. Google Consent Mode v2 — les bandeaux poussent leurs mises à jour dans
 *     le dataLayer sous la forme ['consent', 'update', { ad_user_data: ... }].
 *     On retient la DERNIÈRE valeur connue de `ad_user_data` (le signal Google
 *     dédié à l'envoi de données personnelles aux plateformes publicitaires),
 *     avec repli sur `ad_storage`.
 *  5. TCF v2 (IAB) — `window.__tcfapi` est asynchrone, donc inutilisable au
 *     moment précis de la validation du paiement : on lit l'état mémorisé par
 *     `primeMarketingConsent()`, à appeler au montage de l'application.
 *
 * (la numérotation ci-dessus est aussi l'ordre de priorité)
 */

type ConsentValue = 'granted' | 'denied' | undefined;

/** Dernier état TCF connu, alimenté de façon asynchrone par primeMarketingConsent(). */
let tcfConsent: boolean | undefined;

function readExplicitHook(): boolean | undefined {
  try {
    const hook = (window as unknown as { mondayMindbodyConsent?: unknown }).mondayMindbodyConsent;
    if (typeof hook === 'boolean') return hook;
    if (typeof hook === 'function') return (hook as () => unknown)() === true;
  } catch {
    /* le site peut exposer n'importe quoi : on ignore */
  }
  return undefined;
}

/**
 * Cookiebot : son script uc.js expose `window.Cookiebot` (alias
 * `window.CookieConsent`) avec `hasResponse` (le visiteur s'est prononcé) et
 * `consent.marketing` (catégorie « marketing », celle qui couvre l'envoi de
 * données aux plateformes publicitaires).
 *
 * Lu AVANT la WP Consent API, volontairement : Cookiebot s'installe aussi
 * bien par une extension WordPress que par une balise GTM (cas constaté le
 * 31/07/2026 sur dynamo-bordeaux.com et riise-bordeaux.com, où il est déployé
 * par le conteneur GTM du site). Dans ce cas il n'alimente pas la WP Consent
 * API, et l'extension `wp-consent-api` peut rester installée sans qu'aucun
 * bandeau ne la pilote : la lire en premier ferait ignorer le seul bandeau
 * réellement affiché au visiteur.
 *
 * Tant que le visiteur ne s'est pas prononcé (`hasResponse` faux), on ne
 * conclut pas : les autres signaux décident, et à défaut la réponse reste
 * `false`.
 */
function readCookiebot(): boolean | undefined {
  try {
    const w = window as unknown as {
      Cookiebot?: { hasResponse?: unknown; consent?: { marketing?: unknown } };
      CookieConsent?: { hasResponse?: unknown; consent?: { marketing?: unknown } };
    };
    const cb = w.Cookiebot || w.CookieConsent;
    if (!cb || typeof cb !== 'object') return undefined;
    if (cb.hasResponse !== true) return undefined;
    const marketing = cb.consent?.marketing;
    if (typeof marketing !== 'boolean') return undefined;
    return marketing;
  } catch {
    return undefined;
  }
}

/**
 * Standard WordPress « WP Consent API » : le bandeau du site y déclare le
 * consentement par catégorie, les extensions le lisent via wp_has_consent().
 *
 * ⚠️ Deux pièges, tous deux constatés en production le 30/07/2026 :
 *  - quand AUCUN bandeau ne pilote l'API, `wp_has_consent()` renvoie `true`
 *    (comportement permissif assumé par l'extension). S'y fier reviendrait à
 *    envoyer des données à Meta sur un site sans bandeau.
 *  - avec Complianz + WP Consent API 2.0.1, `window.wp_consent_type` n'est
 *    jamais défini : `wp_has_consent()` retombe donc dans le cas ci-dessus et
 *    répond `true` même après un refus.
 * D'où la lecture directe du cookie, en exigeant qu'un type de consentement
 * soit déclaré.
 */
function readWpConsentApi(): boolean | undefined {
  try {
    const w = window as unknown as {
      wp_consent_type?: unknown;
      consent_api?: { consent_type?: unknown; cookie_prefix?: unknown };
    };

    // Le type de consentement dit qu'un bandeau pilote bien l'API. Selon les
    // versions il est exposé soit en global, soit dans la configuration
    // localisée `consent_api` (cas constaté avec Complianz + WP Consent API
    // 2.0.1, où le global n'est jamais défini).
    const type = String(w.wp_consent_type || w.consent_api?.consent_type || '');
    if (!type) return undefined;

    // On lit le cookie plutôt que d'appeler wp_has_consent() : cette fonction
    // se base sur `window.wp_consent_type`, absent dans les cas ci-dessus, et
    // renvoie alors `true` quoi qu'il arrive. On reproduit donc sa logique à
    // partir d'une source fiable.
    const prefix = String(w.consent_api?.cookie_prefix || 'wp_consent');
    const value = readCookie(`${prefix}_marketing`);
    if (type.indexOf('optout') !== -1) return value !== 'deny';
    return value === 'allow';
  } catch {
    return undefined;
  }
}

function readCookie(name: string): string | undefined {
  try {
    for (const part of document.cookie.split(';')) {
      const entry = part.trim();
      if (entry.indexOf(`${name}=`) === 0) return entry.substring(name.length + 1);
    }
  } catch {
    /* document indisponible */
  }
  return undefined;
}

/**
 * Parcourt le dataLayer à la recherche des commandes `consent` de Google.
 * gtag pousse son objet `arguments`, qui se présente comme un tableau
 * indexé : [0]='consent', [1]='default'|'update', [2]={ ad_user_data: ... }.
 */
function readConsentMode(): boolean | undefined {
  try {
    const dl = (window as unknown as { dataLayer?: unknown[] }).dataLayer;
    if (!Array.isArray(dl)) return undefined;

    let adUserData: ConsentValue;
    let adStorage: ConsentValue;

    for (const entry of dl) {
      if (!entry || typeof entry !== 'object') continue;
      const args = entry as Record<string | number, unknown>;
      if (args[0] !== 'consent') continue;
      const payload = args[2];
      if (!payload || typeof payload !== 'object') continue;
      const state = payload as Record<string, unknown>;
      if (state.ad_user_data === 'granted' || state.ad_user_data === 'denied') {
        adUserData = state.ad_user_data;
      }
      if (state.ad_storage === 'granted' || state.ad_storage === 'denied') {
        adStorage = state.ad_storage;
      }
    }

    const effective = adUserData ?? adStorage;
    return effective === undefined ? undefined : effective === 'granted';
  } catch {
    return undefined;
  }
}

/**
 * À appeler au démarrage de l'application : interroge le bandeau TCF (API
 * asynchrone) et mémorise sa réponse pour que `hasMarketingConsent()` reste
 * synchrone au moment du paiement. Sans bandeau TCF, ne fait rien.
 */
export function primeMarketingConsent(): void {
  try {
    const tcfapi = (window as unknown as { __tcfapi?: (...a: unknown[]) => void }).__tcfapi;
    if (typeof tcfapi !== 'function') return;
    tcfapi('addEventListener', 2, (tcData: unknown, success: boolean) => {
      if (!success || !tcData || typeof tcData !== 'object') return;
      const d = tcData as { gdprApplies?: boolean; purpose?: { consents?: Record<string, boolean> } };
      // Hors périmètre RGPD, le bandeau TCF ne se prononce pas : on laisse les
      // autres signaux décider.
      if (d.gdprApplies === false) return;
      // Finalité 7 : « mesurer la performance des publicités ».
      tcfConsent = d.purpose?.consents?.['7'] === true;
    });
  } catch {
    /* jamais bloquant */
  }
}

/**
 * Consentement marketing du visiteur. `false` par défaut : en l'absence de
 * signal, on ne transmet rien à Meta.
 */
export function hasMarketingConsent(): boolean {
  const explicit = readExplicitHook();
  if (explicit !== undefined) return explicit;

  const cookiebot = readCookiebot();
  if (cookiebot !== undefined) return cookiebot;

  const wpConsent = readWpConsentApi();
  if (wpConsent !== undefined) return wpConsent;

  const consentMode = readConsentMode();
  if (consentMode !== undefined) return consentMode;

  if (tcfConsent !== undefined) return tcfConsent;

  return false;
}
